import { useCallback, useContext, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserContext } from '../UserContext'
import useNotifications from '../../../hooks/useNotifications'
import { getUserContributions } from '../../../services/userService'

export const actions = {
  SET_CONTRIBUTIONS: 'SET_CONTRIBUTIONS',
  CONCAT_CONTRIBUTIONS: 'CONCAT_CONTRIBUTIONS',
  SET_FILTER: 'SET_FILTER',
  SET_ACTIVE_ROLE: 'SET_ACTIVE_ROLE',
}

export const transformContributions = (contributions) => {
  return contributions.map((contribution) => {
    const contributionItem = contribution?.contribution || contribution
    if (contributionItem?.previousNote?.value) {
      contributionItem.previousNote = `${contributionItem.previousNote.value.title}: ${contributionItem.previousNote.value.text}`
    }
    if (contributionItem?.resultingNote?.value) {
      contributionItem.resultingNote = `${contributionItem.resultingNote.value.title}: ${contributionItem.resultingNote.value.text}`
    }
    return contribution
  })
}

export default function useUserContributions() {
  const { t } = useTranslation()
  const { currentUser } = useContext(UserContext)
  const { error } = useNotifications()
  const [rerenderInfiniteScroll, setRerenderInfiniteScroll] = useState()

  function reducer(oldState, action) {
    const pageState = JSON.parse(JSON.stringify(oldState))
    const type = action.type
    if (type === actions.SET_CONTRIBUTIONS) {
      pageState.metadata.filterSelectedItem = { key: 'filterSelectedItem', primaryText: '' }
      handleContributions(pageState, action, false)
      setRerenderInfiniteScroll((prevState) => !prevState)
    } else if (type === actions.CONCAT_CONTRIBUTIONS) {
      pageState.metadata.filterSelectedItem = { key: 'filterSelectedItem', primaryText: '' }
      handleContributions(pageState, action, true)
    } else if (type === actions.SET_FILTER) {
      pageState.metadata.filterSelectedItem = action.payload.filterSelectedItem
      handleContributions(pageState, action, false)
      setRerenderInfiniteScroll((prevState) => !prevState)
    } else if (type === actions.SET_ACTIVE_ROLE) {
      pageState.metadata.activeRole = action.payload
    } else {
      console.error(
        t('user.actionIsNotHandled', {
          defaultValue: `Action is not handled ${JSON.stringify(action)}`,
          message: JSON.stringify(action),
        })
      )
    }
    return pageState
  }
  const [pageState, pageDispatch] = useReducer(reducer, {
    currentUser,
    metadata: {
      filterSelectedItem: { key: 'filterSelectedItem', primaryText: '' },
      filterSuggestions: [],
      persons: {},
      nextPageToken: '',
      activeRole: null,
    },
    contributions: [],
  })

  const getContributions = useCallback(
    (contributorId, fromCommandId, filterSelectedItem, type, subtypes, reducerActionType) => {
      getUserContributions(contributorId, 100, fromCommandId, filterSelectedItem?.secondaryText, type, subtypes)
        .then((data) => {
          const action = {
            type: reducerActionType,
            payload: {
              contributions: transformContributions(data.contributions),
              persons: data.persons,
              nextPageToken: data.nextPageToken,
              filterSelectedItem,
              type,
              subtypes,
            },
          }
          pageDispatch(action)
        })
        .catch((err) => {
          error(err.response.statusText, err)
        })
    },
    [error]
  )

  return { pageState, pageDispatch, getContributions, rerenderInfiniteScroll, setRerenderInfiniteScroll }
}

function handleContributions(pageState, action, concat) {
  if (concat) {
    pageState.contributions = pageState.contributions.concat(action.payload.contributions)
    pageState.persons = pageState.persons.concat(action.payload.persons)
  } else {
    pageState.contributions = action.payload.contributions
    pageState.persons = action.payload.persons
  }
  pageState.metadata.nextPageToken = action.payload.nextPageToken
  pageState.metadata.type = action.payload.type
  pageState.metadata.subtypes = action.payload.subtypes
  addPersonsToCacheAndFilterSuggestions(
    pageState.metadata.persons,
    pageState.metadata.filterSuggestions,
    action.payload.persons
  )
}

function addPersonsToCacheAndFilterSuggestions(cache, filterSuggestions, persons) {
  persons.forEach((person) => {
    cache[person.personId] = { name: person.name, lifespan: person.lifespan }
  })
  filterSuggestions.length = 0 // clearing array
  Object.keys(cache).forEach((personId, index) => {
    filterSuggestions.push({ key: index, primaryText: cache[personId].name, secondaryText: personId })
  })
  filterSuggestions.sort((a, b) => (a.primaryText.toLowerCase() > b.primaryText.toLowerCase() ? 1 : -1))
}
